import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Zap, CheckCircle2, Circle, TrendingUp, BarChart3, Target,
  DollarSign, Users, Briefcase, MapPin, Bell, Mail, FileText, Shield
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
const CATEGORY_ICONS = {
  sales: DollarSign, service: Briefcase, marketing: Mail, hr: Users,
  field_service: MapPin, finance: FileText, operations: BarChart3, retention: Shield,
};
const CATEGORY_COLORS = {
  sales: 'bg-emerald-100 text-emerald-700', service: 'bg-blue-100 text-blue-700',
  marketing: 'bg-purple-100 text-purple-700', hr: 'bg-cyan-100 text-cyan-700',
  field_service: 'bg-amber-100 text-amber-700', finance: 'bg-rose-100 text-rose-700',
  operations: 'bg-gray-100 text-gray-700', retention: 'bg-indigo-100 text-indigo-700',
};

export default function AutomationDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/operations/automation-dashboard`);
      setData(d);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;
  if (!data) return null;

  const pieData = [
    { name: 'Automated', value: data.automated },
    { name: 'Manual', value: data.total_opportunities - data.automated },
  ];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="automation-dashboard-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Zap className="h-5 w-5 text-emerald-500" /></div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Automation Achievement<PageInfoTooltip pageKey="automation" /></h1>
          <p className="text-xs text-gray-400">{data.automated}/{data.total_opportunities} processes automated · {data.active_workflows} active workflows · {data.total_runs} total runs</p>
        </div>
      </div>

      {/* Score Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200 md:col-span-1">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <div className="relative">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="60" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                <circle cx="70" cy="70" r="60" fill="none" stroke="#10B981" strokeWidth="10"
                  strokeDasharray={`${(data.rate / 100) * 377} 377`}
                  strokeLinecap="round" transform="rotate(-90 70 70)" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-emerald-600">{data.rate}%</span>
                <span className="text-[10px] text-gray-500">Automated</span>
              </div>
            </div>
            <p className="text-sm font-semibold text-text-primary mt-3">Automation Score</p>
            <p className="text-[10px] text-gray-400">{data.automated} of {data.total_opportunities} opportunities</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border md:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-sm">By Category</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.by_category} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 9 }} width={80} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="rate" name="Automated" radius={[0, 4, 4, 0]}>
                  {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border md:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Coverage</CardTitle></CardHeader>
          <CardContent className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={4} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  <Cell fill="#10B981" />
                  <Cell fill="#e5e7eb" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.by_category.map(cat => {
          const CatIcon = CATEGORY_ICONS[cat.category] || Zap;
          const catColor = CATEGORY_COLORS[cat.category] || 'bg-gray-100 text-gray-700';
          return (
            <Card key={cat.category} className="bg-white border-gray-border">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center", catColor.split(' ')[0])}>
                    <CatIcon className={cn("h-3.5 w-3.5", catColor.split(' ')[1])} />
                  </div>
                  <span className="text-xs font-semibold capitalize">{cat.category.replace('_', ' ')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold">{cat.rate}%</span>
                  <Badge className={cn("text-[9px]", cat.rate === 100 ? 'bg-emerald-100 text-emerald-700' : cat.rate > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500')}>
                    {cat.automated}/{cat.total}
                  </Badge>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1.5">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${cat.rate}%` }} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* All Opportunities */}
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-500" /> All Automation Opportunities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data.opportunities.map(opp => {
              const CatIcon = CATEGORY_ICONS[opp.category] || Zap;
              return (
                <div key={opp.id} className={cn("flex items-center gap-3 p-2.5 rounded-lg border transition-colors",
                  opp.automated ? "bg-emerald-50/50 border-emerald-100" : "bg-white border-gray-100 hover:border-gray-200"
                )} data-testid={`opp-${opp.id}`}>
                  {opp.automated ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <Circle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  )}
                  <CatIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm font-medium", opp.automated ? "text-emerald-700" : "text-gray-700")}>{opp.name}</p>
                    <p className="text-[10px] text-gray-400">{opp.description}</p>
                  </div>
                  <Badge className={cn("text-[9px] flex-shrink-0",
                    opp.source === 'builtin' ? 'bg-emerald-100 text-emerald-700' :
                    opp.source === 'workflow' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-500'
                  )}>
                    {opp.source === 'builtin' ? 'Built-in' : opp.source === 'workflow' ? 'Workflow' : 'Not Set Up'}
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
